import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight } from "lucide-react";
import AfricaLoudIcon from "./icon-nobg.png";

// -- Brand tokens (identical to home page) ------------------------------------
const BRAND = {
  white: "#ffffff",
  charcoal: "#0b0b0f",
  gray: "#6b7280",
  burgundy: "#800020",
  gold: "#b08d57",
  deepGreen: "#006400",
  lilac: "#a78bfa",
};

// -- Film data -----------------------------------------------------------------
const sampleFilms = [
  {
    id: 1,
    title: "The Boy Who Harnessed the Wind",
    region: "East Africa",
    year: 2019,
    platform: "Netflix",
    genre: "Drama",
    images: [
      "https://images.unsplash.com/photo-1524985069026-dd778a71c7b4?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1524712245354-2c4e5e7121c0?auto=format&fit=crop&w=800&q=70",
    ],
  },
  {
    id: 2,
    title: "Queen Sono",
    region: "Southern Africa",
    year: 2020,
    platform: "Netflix",
    genre: "Action Thriller",
    images: [
      "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1500673922987-e212871fec22?auto=format&fit=crop&w=800&q=70",
    ],
  },
  {
    id: 3,
    title: "Citation",
    region: "West Africa",
    year: 2020,
    platform: "Netflix",
    genre: "Drama",
    images: [
      "https://images.unsplash.com/photo-1524712245354-2c4e5e7121c0?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1524985069026-dd778a71c7b4?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=70",
    ],
  },
  {
    id: 4,
    title: "Nairobi Half Life",
    region: "East Africa",
    year: 2012,
    platform: "Showmax",
    genre: "Crime Drama",
    images: [
      "https://images.unsplash.com/photo-1500673922987-e212871fec22?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1524985069026-dd778a71c7b4?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?auto=format&fit=crop&w=800&q=70",
    ],
  },
  {
    id: 5,
    title: "Blood & Water",
    region: "Southern Africa",
    year: 2020,
    platform: "Netflix",
    genre: "Teen Drama",
    images: [
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1524712245354-2c4e5e7121c0?auto=format&fit=crop&w=800&q=70",
      "https://images.unsplash.com/photo-1500673922987-e212871fec22?auto=format&fit=crop&w=800&q=70",
    ],
  },
];

// -- Shared primitives (identical to home page) --------------------------------

function Rule({ className = "" }) {
  return <div className={`h-px w-full bg-black/10 ${className}`} />;
}

function Box({ children, className = "" }) {
  return (
    <div className={`border border-black/15 bg-white ${className}`}>
      {children}
    </div>
  );
}

function TagPill({ children, tone = "neutral" }) {
  const style =
    tone === "deepGreen"
      ? {
          borderColor: `${BRAND.deepGreen}55`,
          backgroundColor: `${BRAND.deepGreen}10`,
          color: BRAND.deepGreen,
        }
      : tone === "burgundy"
      ? {
          borderColor: `${BRAND.burgundy}55`,
          backgroundColor: `${BRAND.burgundy}08`,
        }
      : tone === "gold"
      ? {
          borderColor: `${BRAND.gold}55`,
          backgroundColor: `${BRAND.gold}10`,
        }
      : {};

  return (
    <span
      style={style}
      className="inline-flex items-center border px-2 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold"
    >
      {children}
    </span>
  );
}

// -- Site Header � exact copy from home page, "film" tab active ----------------
function SiteHeader() {
  const navLinks = [
    { key: "home", label: "Home", to: "/" },
    { key: "culture", label: "Culture X Music", to: "/culture-music" },
    { key: "fashion", label: "Art X Fashion", to: "/art-fashion" },
    { key: "film", label: "African Film", to: "/film" },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white">
      <Helmet>
        <title>African Film Atlas | Africa Loud</title>
      </Helmet>

      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-5 sm:pt-6">

          {/* Top links */}
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <Link
                to="/culture-music"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Music X Culture
              </Link>
              <Link
                to="/art-fashion"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Art X Fashion
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </Link>
              <Link
                to="/login"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Login
              </Link>
            </div>
          </div>

          <Rule className="mt-4" />

          {/* Logo row */}
          <div className="grid grid-cols-1 items-center gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">
                Culture intelligence &middot; creative execution
              </div>
            </div>

            <Link
              to="/"
              className="mx-auto flex items-center gap-4 text-center"
              aria-label="Go to Home"
            >
              <img
                src={AfricaLoudIcon}
                alt="AL"
                className="w-10 h-10 object-contain sm:w-12 sm:h-12"
                onError={(e) => (e.target.style.display = "none")}
              />
              <div className="text-left">
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl leading-none">
                  Africa Loud
                </div>
                <div className="mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </Link>

            <div className="hidden justify-self-end sm:block text-right">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-400">
                JHB / LAG / KGL
              </div>
            </div>
          </div>

          <Rule />

          {/* Nav bar � "African Film" active */}
          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex flex-wrap items-center gap-2">
              {navLinks.map(({ key, label, to }) => (
                <Link
                  key={key}
                  to={to}
                  className={`px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition ${
                    key === "film"
                      ? "bg-black text-white"
                      : "bg-white text-black hover:bg-black/5"
                  }`}
                >
                  {label}
                </Link>
              ))}
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Subscribe
              </div>
              <div className="h-3 w-px bg-black/15" />
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

// -- Site Footer � exact copy from home page -----------------------------------
function SiteFooter() {
  const links = [
    { label: "Instagram", href: "https://instagram.com/africaloudmag", external: true },
    { label: "Twitter", href: "https://twitter.com/africaloudmag", external: true },
    { label: "TikTok", href: "https://tiktok.com/@africaloudmag", external: true },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/terms" },
    { label: "Collab", href: "/collab" },
  ];

  return (
    <footer className="mt-12 bg-neutral-900 px-5 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-lg font-bold text-white font-serif">
            Africa Loud
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/10 text-[10px] tracking-widest uppercase text-white/40">
          &copy; 2026 Africa Loud. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// -- Sponsor Strip � LILAC accent ----------------------------------------------
function SponsorStrip() {
  return (
    <div className="mt-6">
      <Box className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
              Partner
            </div>
            <div className="mt-1 font-serif text-xl tracking-tight text-neutral-950">
              ON THIRD - Premium Luxury
            </div>
            <div className="mt-1 text-sm text-neutral-700">
              Shop the scent with a 10% off offer via the partner link.
            </div>
          </div>
          <a
            href="https://onthird.co.za/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase hover:opacity-80 transition"
            style={{ borderColor: BRAND.lilac, color: BRAND.lilac }}
          >
            Shop the scent &middot; 10% off
            <ArrowRight className="ml-2 h-3 w-3" />
          </a>
        </div>
      </Box>
    </div>
  );
}

// -- Film Card � with image carousel ------------------------------------------
function FilmCard({ film }) {
  const [index, setIndex] = useState(0);

  const prev = (e) => {
    e.stopPropagation();
    setIndex((p) => (p - 1 + film.images.length) % film.images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setIndex((p) => (p + 1) % film.images.length);
  };

  return (
    <Box className="overflow-hidden transition-all duration-300 hover:border-black/30 hover:shadow-lg group">
      {/* Image carousel */}
      <div className="relative h-52 overflow-hidden bg-neutral-100">
        <img
          src={film.images[index]}
          alt={film.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />

        {/* Gradient overlay at bottom for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        {/* Carousel controls */}
        <button
          onClick={prev}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-black/60 text-white text-sm hover:bg-black transition"
          aria-label="Previous image"
        >
           &lsaquo;
        </button>
        <button
          onClick={next}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-black/60 text-white text-sm hover:bg-black transition"
          aria-label="Next image"
        >
           &rsaquo;
        </button>

        {/* Dot indicators */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
          {film.images.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setIndex(i); }}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{
                backgroundColor: i === index ? "#fff" : "rgba(255,255,255,0.45)",
              }}
              aria-label={`Image ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Card body */}
      <div className="p-5">
        <TagPill tone="deepGreen">{film.region}</TagPill>

        <h2 className="font-serif text-2xl leading-snug tracking-tight text-neutral-950 mt-3 group-hover:text-neutral-700 transition-colors">
          {film.title}
        </h2>

        <p className="text-sm text-gray-500 mt-1">
          {film.genre} &middot; {film.year}
        </p>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-black/[0.08]">
          <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-neutral-400">
            Platform
          </span>
          <span className="text-sm font-semibold text-neutral-950">
            {film.platform}
          </span>
        </div>
      </div>
    </Box>
  );
}

// -- Page ----------------------------------------------------------------------
export default function AfricanFilmAtlas() {
  const [genre, setGenre] = useState("All");
  const [region, setRegion] = useState("All");

  const genres = useMemo(
    () => ["All", ...new Set(sampleFilms.map((f) => f.genre))],
    []
  );
  const regions = useMemo(
    () => ["All", ...new Set(sampleFilms.map((f) => f.region))],
    []
  );

  const filteredFilms = useMemo(
    () =>
      sampleFilms.filter(
        (f) =>
          (genre === "All" || f.genre === genre) &&
          (region === "All" || f.region === region)
      ),
    [genre, region]
  );

  return (
    <div className="bg-white text-neutral-950 min-h-screen">

      {/* Fonts � exact same <style> block as home page */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;700;800&family=Outfit:wght@300;400;600;700&display=swap');

        body, div, p, span, button, a, select {
          font-family: 'Outfit', sans-serif;
        }

        .font-serif, h1, h2, h3, h4 {
          font-family: 'Fraunces', serif !important;
          font-weight: 700;
        }
      `}</style>

      <SiteHeader />

      {/* Page hero */}
      <div className="border-b border-black/10 py-12 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div
            className="h-2 w-2"
            style={{ backgroundColor: BRAND.deepGreen }}
          />
          <span className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-600">
            African Film
          </span>
        </div>
        <h1
          className="font-serif text-5xl sm:text-6xl leading-[1.05]"
          style={{ color: BRAND.deepGreen }}
        >
          African Film Atlas
        </h1>
        <p className="mt-3 text-neutral-600 text-base">
          Mapping African cinema through region, genre and story
        </p>
      </div>

      {/* Filters + grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-neutral-400">
            Filter
          </span>
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            className="border border-black/15 px-3 py-2 text-[11px] uppercase tracking-[0.12em] font-semibold bg-white hover:border-black/30 transition outline-none cursor-pointer"
          >
            {genres.map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="border border-black/15 px-3 py-2 text-[11px] uppercase tracking-[0.12em] font-semibold bg-white hover:border-black/30 transition outline-none cursor-pointer"
          >
            {regions.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </div>

        {filteredFilms.length > 0 ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filteredFilms.map((f) => (
              <FilmCard key={f.id} film={f} />
            ))}
          </div>
        ) : (
          <div className="border border-black/10 py-16 text-center text-[11px] uppercase tracking-[0.18em] text-neutral-400">
            No films match this filter
          </div>
        )}

        <Rule className="mt-12" />
        <SponsorStrip />
      </div>

      <SiteFooter />
    </div>
  );
}